import { $rqCustomizationApiClient } from "@/api/customization"
import { query } from "@/api/query"
import { deploymentPath } from "@/config"
import { MY_SPACE } from "@/constants/spaces"
import { AuthClientInstance, queryClient, SpaceStorageInstance, themeManagerInstance } from "@/lib/services/instances"

// Only the network half of the palette lives here. The synchronous half (whitelabel colors from the auth
// customization, plus the space-scoped storage the manager restores from) stays with `themeManagerInstance`,
// so a palette is already applied before the first paint and there's no flash of the default teal accent.

// Sync the palette with a given spaceId. If the palette is not found, fallback to the default palette.
async function setPaletteBySpaceId(spaceId: string) {
  const paletteQueryOptions = $rqCustomizationApiClient.queryOptions(
    "get",
    "/v1/customizable/colors/{organization_id}",
    { params: { path: { organization_id: spaceId } } },
    { retry: false }
  )

  try {
    const colorsData = await queryClient.query({ ...paletteQueryOptions, staleTime: 0 })
    themeManagerInstance.setPalette({
      hex: colorsData.color as `#${string}`,
      useClosestAccessibleColor: colorsData.readability
    })
  } catch {
    themeManagerInstance.setPalette("mkr")
  }
}

// Resolve the palette for the current space from the network.
async function syncPalette() {
  const spaceId = SpaceStorageInstance.getSpace()
  const spaces = await queryClient.query({ ...query.space.list, staleTime: "static" })
  const space = spaces.find(sp => sp.id === spaceId) || MY_SPACE

  if (space.space !== "pro") {
    themeManagerInstance.setPalette("mkr")
    return
  }

  await setPaletteBySpaceId(spaceId)
}

/**
 * Resolve the accent palette from the network and, in the Arduino environment, keep it in sync with the
 * active space.
 *
 * 💡 NOTE: The sync must NOT be awaited, otherwise the application gets blocked during the initial render.
 */
export function initPaletteSync() {
  if (deploymentPath.customKey) {
    // Guaranteed by `themeManagerInstance`, which throws when a whitelabel domain has no customization.
    const customization = AuthClientInstance.getCustomization()
    if (customization) setPaletteBySpaceId(customization.organization_id)
    return
  }

  syncPalette()
  SpaceStorageInstance.subscribe((_, key) => {
    if (key === "space-id") syncPalette()
  })
}
